import type {
  CMSComponentResponsiveImage,
  ResponsiveImageSize,
  SanityImage,
} from '@/types/sanity'

type ResponsiveImageExtension = 'webp' | 'avif'
export type ResponsiveImageSizeProp = ResponsiveImageSize | `custom:${number}`

const PRESET_SIZES: Record<ResponsiveImageSize, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1200,
  '2xl': 1536,
  max: 2000,
}

type GetTransformationProxyUrlProps = {
  source: SanityImage
  variant: 'desktop' | 'mobile'
  size: ResponsiveImageSizeProp
  targetFormat: ResponsiveImageExtension
  documentId: string
}

export const getTransformationProxyUrl = ({
  source,
  variant,
  size,
  targetFormat,
  documentId,
}: GetTransformationProxyUrlProps) => {
  if (!source?.asset?.url) return ''

  if (import.meta.env.DISABLE_IMAGE_PROXY === true) return source.asset.url
  const version = +new Date(source.asset._updatedAt)
  return `/cms-image/${variant}/${size}/${version}/${documentId}.${targetFormat}`
}

export const generateImageSrcSetByResponsiveImage = (
  node: CMSComponentResponsiveImage,
  targetFormat: ResponsiveImageExtension,
  customSizes?: number[],
) => {
  const { desktopImage, _id: imageDocumentId } = node
  if (customSizes?.length) {
    return [...customSizes]
      .reverse()
      .map(
        (size) =>
          `${getTransformationProxyUrl({
            source: desktopImage,
            variant: 'desktop',
            size: `custom:${size}`,
            documentId: imageDocumentId,
            targetFormat,
          })} ${size}w`,
      )
      .join(', ')
  }
  const descendingSizeEntries = Object.entries(PRESET_SIZES).sort(
    ([, a], [, b]) => b - a,
  ) as [ResponsiveImageSize, number][]
  return descendingSizeEntries
    .map(
      ([size, width]) =>
        `${getTransformationProxyUrl({
          source: desktopImage,
          variant: 'desktop',
          size,
          documentId: imageDocumentId,
          targetFormat,
        })} ${width}w`,
    )
    .join(', ')
}

export const getImageSizes = (customSizes?: number[]) => {
  if (customSizes) {
    return customSizes
      .map((width, index, { length }) => {
        if (length - 1 === index) return `${width}px`
        return `(max-width: ${width - 1}px) ${width}px`
      })
      .join(', ')
  }
  const ascendingSizeEntries = Object.entries(PRESET_SIZES).sort(
    ([, a], [, b]) => a - b,
  ) as [ResponsiveImageSize, number][]
  return ascendingSizeEntries
    .map(([_size, width], index, { length }) => {
      if (length - 1 === index) return `${width}px`
      return `(max-width: ${width - 1}px) ${width}px`
    })
    .join(', ')
}
